import i18n from 'helpers/i18n';
import messages from './messages';
import { useQuery } from '@tanstack/react-query';
import { getAllStarred } from 'actions/starred';
import { getEducatorRecommendations } from 'actions/coachings';
import { completedRecommended } from 'actions/recommendations';
import { getGettingStarted } from 'actions/learningResourcesGettingStarted';
import { getViewingHistory } from 'actions/learningResourcesViewingHistory';
import { FilterState } from 'pages/LearningResources/FilterSideBar/FilterSideBar';
import { RecommendationMetadata } from 'types/api/ClassScoresRecommendation';
import { LearningResourceSearchResult } from 'types/api/LearningResource';

import {
  getAllLearningResources,
  getClassroomScores,
  getLearningResourceMyWatchedResources,
  getRecommendedLearningResources,
  getLastViewedLearningResources
} from 'actions/learningResources';

export type LrAction =
  | 'allResources'
  | 'recommended'
  | 'gettingStarted'
  | 'viewingHistory'
  | 'important'
  | 'lastViewed'
  | 'completedResources'
  | 'completedRecommended'
  | 'classroomScores'
  | 'recommendedResources';

export type LrQueryResult = {
  resources: LearningResourceSearchResult[];
  totalPages: number;
  recommendation: RecommendationMetadata | null;
};

type FetchParams = {
  action: LrAction;
  filters?: FilterState;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page: number;
  perPage: number;
  educatorId?: number;
  educatorGuid?: string;
};

async function fetchForAction(params: FetchParams): Promise<LrQueryResult> {
  const {
    action,
    filters,
    sortBy,
    sortDir,
    page,
    perPage,
    educatorId,
    educatorGuid
  } = params;

  const apiFilters = filters ?? {};
  const sortAndFilters = { sort_by: sortBy, sort_dir: sortDir, ...apiFilters };

  switch (action) {
    case 'allResources': {
      const res = await getAllLearningResources({
        page,
        per_page: perPage,
        ...sortAndFilters
      });
      return {
        resources: res.data.resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    case 'recommended': {
      const res = await getRecommendedLearningResources({
        per_page: perPage,
        page,
        ...sortAndFilters
      });
      const resources = res.data.recommendations.map(
        r => r.resourceable as unknown as LearningResourceSearchResult
      );
      return {
        resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    case 'gettingStarted': {
      const res = await getGettingStarted(perPage, page, sortAndFilters as any);
      return {
        resources: res.data.resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    case 'viewingHistory': {
      const res = await getViewingHistory(perPage, page, sortAndFilters as any);
      return {
        resources: res.data.resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    case 'important': {
      const res = await getAllStarred(perPage, page, sortAndFilters as any);
      return {
        resources: res.data.learning_resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    case 'lastViewed': {
      const res = await getLastViewedLearningResources(
        perPage,
        page,
        sortAndFilters as any
      );
      return {
        resources: res.data.learning_resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    case 'completedResources': {
      const res = await getLearningResourceMyWatchedResources({
        page,
        per_page: perPage,
        ...sortAndFilters
      } as any);
      return {
        resources: res.data.resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    case 'completedRecommended': {
      const res = await completedRecommended(
        educatorId,
        perPage,
        page,
        sortAndFilters as any
      );
      return {
        resources: res.data.resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    case 'classroomScores': {
      const res = await getClassroomScores(educatorId, page, perPage);
      return {
        resources: res.data
          .resources as unknown as LearningResourceSearchResult[],
        totalPages: (res.data as any).pagination?.total_pages || 1,
        recommendation: res.data.recommendation
      };
    }

    case 'recommendedResources': {
      const res = await getEducatorRecommendations(
        educatorGuid,
        perPage,
        page,
        sortAndFilters as any
      );
      return {
        resources: res.data.resources,
        totalPages: res.data.pagination?.total_pages || 1,
        recommendation: null
      };
    }

    default:
      throw new Error(`Unknown action: ${action}`);
  }
}

type UseLearningResourcesParams = FetchParams & { enabled?: boolean };

export function useLearningResources({
  action,
  filters,
  sortBy,
  sortDir,
  page,
  perPage,
  educatorId,
  educatorGuid,
  enabled = true
}: UseLearningResourcesParams) {
  const params: FetchParams = {
    action,
    filters,
    sortBy,
    sortDir,
    page,
    perPage,
    educatorId,
    educatorGuid
  };

  return useQuery({
    queryKey: ['learning-resources', params],
    queryFn: () => fetchForAction(params),
    enabled
  });
}

export function getLrTitle(action: LrAction, educatorName?: string): string {
  switch (action) {
    case 'allResources':
      return i18n.ft(messages.allResources);
    case 'recommended':
      return i18n.ft(messages.recommendedTo);
    case 'gettingStarted':
      return i18n.ft(messages.gettingStarted);
    case 'viewingHistory':
      return i18n.ft(messages.viewingHistory);
    case 'important':
      return i18n.ft(messages.important);
    case 'lastViewed':
      return i18n.ft(messages.lastViewed);
    case 'completedResources':
      return i18n.ft(messages.completedResources);
    case 'completedRecommended':
      return i18n.ft(messages.completedRecommended, { educator: educatorName });
    case 'classroomScores':
      return i18n.ft(messages.classroomScores, { educator: educatorName });
    case 'recommendedResources':
      return i18n.ft(messages.recommendations, { educator: educatorName });
    default:
      return '';
  }
}
